/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import { find, findIndex, get } from 'lodash';

import { contexts, hooks, unstable, utils } from '@broadleaf/admin-components';

const { ToastContainerContext } = contexts;
const { useHasAccess } = hooks;
const { Button } = unstable;
const { log, RequestUtils: Request } = utils;
const logger = log.getLogger('fleet.components.customerAssociation.rowAction');

const CustomerAssociationRowAction = props => {
  const { actionDefinition, row } = props;
  const hasAccess = useHasAccess(
    props.actionDefinition.scope,
    props.actionDefinition.operationType
  );
  const disabled = props.disabled || props.loading || !hasAccess;
  const hasResendEndpoint = !!~findIndex(actionDefinition.endpoints, [
    'name',
    'RESEND'
  ]);
  const hasReenableEndpoint = !!~findIndex(actionDefinition.endpoints, [
    'type',
    'REENABLE'
  ]);

  if (row.status === 'Expired' && hasResendEndpoint) {
    return <Resend {...props} disabled={disabled} />;
  }

  if (row.status === 'Revoked' && hasReenableEndpoint) {
    return <Reenable {...props} disabled={disabled} />;
  }

  if (row.status !== 'Revoked') {
    return <Revoke {...props} disabled={disabled} />;
  }

  return null;
};

const Resend = ({
  actionDefinition,
  collectionState: { actions },
  contextParams,
  disabled,
  row
}) => {
  const endpoint = find(actionDefinition.endpoints, ['type', 'RESEND']);
  const successMessage = get(
    actionDefinition,
    'attributes.resendSuccess',
    'Auth code resent'
  );
  const failureMessage = get(
    actionDefinition,
    'attributes.resendFailure',
    'Failed to resend auth code'
  );
  const { addToast } = React.useContext(ToastContainerContext);

  async function doResend(id) {
    const mergedContextParams = Request.mergeContextParams([
      {
        id,
        row,
        values: row
      },
      contextParams
    ]);

    const mergedConfig = Request.mergeRequestConfigs(
      [{ data: row }, endpoint],
      mergedContextParams
    );

    try {
      const { data } = await Request.request(mergedConfig, mergedContextParams);
      addToast(successMessage, Toasts.RESEND.SUCCESS);
      actions.hydrateItem(row.id, data);
    } catch (err) {
      logger.error(
        `Error encountered resending auth code for FleetCustomer ${row.id}`,
        err
      );
      addToast(failureMessage, Toasts.RESEND.FAILURE);
    }
  }

  return (
    <Button
      className="tw-font-bold tw-leading-none"
      color="primary"
      disabled={disabled}
      onClick={() => doResend(row.id)}
      size="sm"
      type="button"
    >
      {get(actionDefinition, 'attributes.resendLabel', 'Resend')}
    </Button>
  );
};

const Reenable = ({
  actionDefinition,
  collectionState: { actions },
  contextParams,
  disabled,
  row
}) => {
  const endpoint = find(actionDefinition.endpoints, ['type', 'REENABLE']);
  const successMessage = get(
    actionDefinition,
    'attributes.reenableSuccess',
    'Auth code re-enabled'
  );
  const failureMessage = get(
    actionDefinition,
    'attributes.resendFailure',
    'Failed to re-enable auth code'
  );
  const { addToast } = React.useContext(ToastContainerContext);

  async function doResend(id) {
    const mergedContextParams = Request.mergeContextParams([
      {
        id,
        row,
        values: row
      },
      contextParams
    ]);

    const mergedConfig = Request.mergeRequestConfigs(
      [{ data: row }, endpoint],
      mergedContextParams
    );

    try {
      const { data } = await Request.request(mergedConfig, mergedContextParams);
      addToast(successMessage, Toasts.REENABLE.SUCCESS);
      actions.hydrateItem(row.id, data);
    } catch (err) {
      logger.error(
        `Error encountered re-enabling auth code for FleetCustomer ${row.id}`,
        err
      );
      addToast(failureMessage, Toasts.REENABLE.FAILURE);
    }
  }

  return (
    <Button
      className="tw-font-bold tw-leading-none"
      color="primary"
      disabled={disabled}
      onClick={() => doResend(row.id)}
      size="sm"
      type="button"
    >
      {get(actionDefinition, 'attributes.reenableLabel', 'Re-Enable')}
    </Button>
  );
};

const Revoke = ({
  actionDefinition,
  collectionState: { actions },
  contextParams,
  disabled,
  row
}) => {
  const endpoint = find(actionDefinition.endpoints, ['type', 'REVOKE']);
  const successMessage = get(
    actionDefinition,
    'attributes.revokeSuccess',
    'Revocation succeeded'
  );
  const failureMessage = get(
    actionDefinition,
    'attributes.revokeFailure',
    'Revocation failed'
  );
  const { addToast } = React.useContext(ToastContainerContext);

  async function doRevoke(id) {
    const mergedContextParams = Request.mergeContextParams([
      {
        id,
        row,
        values: row
      },
      contextParams
    ]);

    const mergedConfig = Request.mergeRequestConfigs(
      [{ data: row }, endpoint],
      mergedContextParams
    );

    try {
      const { data } = await Request.request(mergedConfig, mergedContextParams);
      actions.hydrateItem(row.id, data);
      addToast(successMessage, Toasts.REVOKE.SUCCESS);
    } catch (err) {
      logger.error(
        `Error encountered revoking auth code for FleetCustomer ${row.id}`,
        err
      );
      addToast(failureMessage, Toasts.REVOKE.FAILURE);
    }
  }

  return (
    <Button
      className="tw-font-bold tw-leading-none"
      color="danger"
      disabled={disabled}
      onClick={() => doRevoke(row.id)}
      size="sm"
      type="button"
    >
      {get(actionDefinition, 'attributes.revokeLabel', 'Revoke')}
    </Button>
  );
};

const Toasts = Object.freeze({
  RESEND: {
    SUCCESS: {
      autoClose: 3000,
      closeOnClick: true,
      progress: 1,
      showProgress: false,
      type: 'success'
    },
    FAILURE: {
      autoClose: 3000,
      closeOnClick: true,
      progress: 0,
      showProgress: false,
      type: 'error'
    }
  },
  REVOKE: {
    SUCCESS: {
      autoClose: 3000,
      closeOnClick: true,
      progress: 1,
      showProgress: false,
      type: 'success'
    },
    FAILURE: {
      autoClose: 3000,
      closeOnClick: true,
      progress: 0,
      showProgress: false,
      type: 'error'
    }
  },
  REENABLE: {
    SUCCESS: {
      autoClose: 3000,
      closeOnClick: true,
      progress: 1,
      showProgress: false,
      type: 'success'
    },
    FAILURE: {
      autoClose: 3000,
      closeOnClick: true,
      progress: 0,
      showProgress: false,
      type: 'error'
    }
  }
});

export default CustomerAssociationRowAction;
export { CustomerAssociationRowAction };
